const NUMERIC_INPUT_SELECTOR = 'input[inputmode="numeric"]';
const PHONE_MASK_SELECTOR = '[data-phone-mask]';
const FORM_SELECTOR = '.form-section__form';
const FIELD_SELECTOR = '.form-section__field';
const ERROR_SELECTOR = '.form-section__error';
const STATUS_SELECTOR = '.form-section__status';
const SUBMIT_SELECTOR = '.form-section__submit';
const CUSTOM_SELECT_SELECTOR = '[data-custom-select]';
const CUSTOM_SELECT_TEXT_SELECTOR = '[data-custom-select-text]';
const RECAPTCHA_RESPONSE_SELECTOR = '#g-recaptcha-response, input[name="g-recaptcha-response"]';
const RECAPTCHA_SCRIPT_SELECTOR = 'script[src*="google.com/recaptcha/enterprise.js"]';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    const parts = [
        digits.slice(0, 3),
        digits.slice(3, 6),
        digits.slice(6, 8),
        digits.slice(8, 10),
    ].filter(Boolean);

    return parts.join(' ');
};

const initNumericInputs = () => {
    const inputs = document.querySelectorAll(NUMERIC_INPUT_SELECTOR);

    inputs.forEach((input) => {
        input.addEventListener('input', () => {
            if (input.matches(PHONE_MASK_SELECTOR)) {
                input.value = formatPhone(input.value);
                return;
            }

            input.value = input.value.replace(/\D/g, '');
        });
    });
};

const closeCustomSelect = (select) => {
    const trigger = select.querySelector('.form-section__select-trigger');

    select.classList.remove('is-open');

    if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
    }
};

const closeOtherCustomSelects = (currentSelect) => {
    document.querySelectorAll(CUSTOM_SELECT_SELECTOR).forEach((select) => {
        if (select !== currentSelect) {
            closeCustomSelect(select);
        }
    });
};

const initCustomSelects = () => {
    const selects = document.querySelectorAll(CUSTOM_SELECT_SELECTOR);

    selects.forEach((select) => {
        const nativeSelect = select.querySelector('select');
        const trigger = select.querySelector('.form-section__select-trigger');
        const text = select.querySelector(CUSTOM_SELECT_TEXT_SELECTOR);
        const options = select.querySelectorAll('.form-section__select-option');

        if (!nativeSelect || !trigger || !text || !options.length) {
            return;
        }

        const selectedOption = nativeSelect.options[nativeSelect.selectedIndex];

        if (selectedOption?.value) {
            text.textContent = selectedOption.textContent;
        }

        trigger.addEventListener('click', () => {
            const isOpen = select.classList.contains('is-open');

            closeOtherCustomSelects(select);
            select.classList.toggle('is-open', !isOpen);
            trigger.setAttribute('aria-expanded', String(!isOpen));
        });

        options.forEach((option) => {
            option.addEventListener('click', () => {
                nativeSelect.value = option.dataset.value;
                text.textContent = option.textContent;
                nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
                closeCustomSelect(select);
            });
        });
    });

    document.addEventListener('click', (event) => {
        if (event.target.closest(CUSTOM_SELECT_SELECTOR)) {
            return;
        }

        closeOtherCustomSelects(null);
    });
};

const setFieldError = (field, message) => {
    const error = field.querySelector(ERROR_SELECTOR);

    field.classList.add('is-error');

    if (error) {
        error.textContent = message;
    }
};

const clearFieldError = (field) => {
    const error = field.querySelector(ERROR_SELECTOR);

    field.classList.remove('is-error');

    if (error) {
        error.textContent = '';
    }
};

const getFieldValue = (field) => {
    const checkedInput = field.querySelector('input[type="checkbox"], input[type="radio"]');

    if (checkedInput?.type === 'checkbox') {
        return checkedInput.checked ? checkedInput.value : '';
    }

    if (checkedInput?.type === 'radio') {
        const selected = field.querySelector(`input[name="${checkedInput.name}"]:checked`);
        return selected ? selected.value : '';
    }

    const control = field.querySelector('input, textarea, select');

    return control ? control.value.trim() : '';
};

const validateField = (field) => {
    const control = field.querySelector('input, textarea, select');

    if (!control) {
        return true;
    }

    const value = getFieldValue(field);

    if (control.required && !value) {
        setFieldError(field, control.dataset.errorMessage || 'Bu alan zorunludur.');
        return false;
    }

    if (control.type === 'email' && value && !EMAIL_REGEX.test(value)) {
        setFieldError(field, control.dataset.emailMessage || 'Geçerli bir e-posta adresi yazınız.');
        return false;
    }

    clearFieldError(field);
    return true;
};

const setFormStatus = (form, type, message) => {
    const status = form.querySelector(STATUS_SELECTOR);

    if (!status) {
        return;
    }

    status.classList.remove('is-success', 'is-error');
    status.textContent = '';

    if (!message) {
        return;
    }

    status.classList.add(type === 'success' ? 'is-success' : 'is-error');
    status.textContent = message;
};

const getRecaptchaSiteKeyFromScript = () => {
    const script = document.querySelector(RECAPTCHA_SCRIPT_SELECTOR);

    if (!script?.src) {
        return '';
    }

    try {
        return new URL(script.src).searchParams.get('render') || '';
    } catch (error) {
        return '';
    }
};

const getRecaptchaToken = (form) => new Promise((resolve, reject) => {
    const siteKey = form.dataset.recaptchaSiteKey || getRecaptchaSiteKeyFromScript();
    const recaptcha = window.grecaptcha?.enterprise;

    if (!siteKey || !recaptcha) {
        reject(new Error('reCAPTCHA is not available'));
        return;
    }

    recaptcha.ready(() => {
        recaptcha.execute(siteKey, { action: form.dataset.recaptchaAction })
            .then(resolve)
            .catch(reject);
    });
});

const setSubmitButtonLoading = (submitButton, isLoading, text) => {
    if (!submitButton) {
        return;
    }

    submitButton.classList.toggle('is-loading', isLoading);
    submitButton.disabled = isLoading;

    if (text) {
        submitButton.textContent = text;
    }
};

const prepareRecaptcha = async (form) => {
    if (!form.dataset.recaptchaAction) {
        return true;
    }

    const responseInput = form.querySelector(RECAPTCHA_RESPONSE_SELECTOR);
    const submitButton = form.querySelector(SUBMIT_SELECTOR);
    const defaultButtonText = submitButton?.textContent;

    if (!responseInput) {
        setFormStatus(form, 'error', form.dataset.submitErrorMessage);
        return false;
    }

    setSubmitButtonLoading(submitButton, true, 'Gönderiliyor');

    try {
        responseInput.value = await getRecaptchaToken(form);
        return true;
    } catch (error) {
        responseInput.value = '';
        setFormStatus(form, 'error', form.dataset.submitErrorMessage);
        return false;
    } finally {
        setSubmitButtonLoading(submitButton, false, defaultButtonText);
    }
};

const submitForm = async (form) => {
    const endpoint = form.getAttribute('action');
    const method = form.getAttribute('method') || 'post';
    const submitButton = form.querySelector(SUBMIT_SELECTOR);
    const defaultButtonText = submitButton?.textContent;

    if (!endpoint || endpoint === '#') {
        return;
    }

    setFormStatus(form, null, '');

    setSubmitButtonLoading(submitButton, true, 'Gönderiliyor');

    try {
        const response = await fetch(endpoint, {
            method: method.toUpperCase(),
            body: new FormData(form),
            headers: {
                Accept: 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Form submit failed');
        }

        form.reset();
        setFormStatus(form, 'success', form.dataset.successMessage);
    } catch (error) {
        setFormStatus(form, 'error', form.dataset.submitErrorMessage);
    } finally {
        if (submitButton) {
            setSubmitButtonLoading(submitButton, false, defaultButtonText);
        }
    }
};

const initFormValidation = () => {
    const forms = document.querySelectorAll(FORM_SELECTOR);

    forms.forEach((form) => {
        const fields = form.querySelectorAll(FIELD_SELECTOR);

        fields.forEach((field) => {
            field.addEventListener('input', () => clearFieldError(field));
            field.addEventListener('change', () => clearFieldError(field));
        });

        form.addEventListener('submit', async (event) => {
            const isValid = Array.from(fields).map(validateField).every(Boolean);

            event.preventDefault();

            if (!isValid) {
                setFormStatus(form, null, '');
                return;
            }

            const hasRecaptchaToken = await prepareRecaptcha(form);

            if (!hasRecaptchaToken) {
                return;
            }

            submitForm(form);
        });
    });
};

document.addEventListener('DOMContentLoaded', initNumericInputs);
document.addEventListener('DOMContentLoaded', initCustomSelects);
document.addEventListener('DOMContentLoaded', initFormValidation);
